import { useState } from 'react';
import { useRecoilValue } from 'recoil';

import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/Poll';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { MenuItemConfig } from 'common/types';
import { getDashboardUrlState } from 'components/common/Managers/RouteStateManager.state';

import Dropdown from 'components/common/Dropdown/Dropdown';
import Link from 'components/common/Link/Link';
import DashboardCreator, { AFTER_CREATE } from 'components/dashboards/DashboardCreator';
import { dashboardListState } from 'components/account/account.state';

type NavBarDashboardsDropdownProps = {
  isActive: boolean;
  readOnly: boolean;
  testId?: ELEMENT;
};

/**
 * Dashboards dropdown for the main navbar
 *
 * @prop isActive         If true, adds the 'active' class to indicate an active navbar section
 * @prop readOnly         Indicates if the user has ready only permissions. False implies that user has write permissions.
 * @prop testId           Test id for testing
 *
 * @returns {Fragment}    The Dashboards dropdown component
 */

const NavBarDashboardsDropdown = ({ isActive, readOnly, testId }: NavBarDashboardsDropdownProps): JSX.Element => {
  const dashboardList = useRecoilValue(dashboardListState);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const getDashboardUrl = useRecoilValue(getDashboardUrlState);

  const topSection: MenuItemConfig[] = [];
  if (!readOnly) {
    topSection.push({
      clickHandler: (): void => setShowCreateDialog(true),
      testId: ELEMENT.navDashboardsNewDashboard,
      as: 'button',
      children: (
        <>
          <AddIcon fontSize="small" />
          {' New Dashboard'}
        </>
      ),
      text: 'New Dashboard',
    } as unknown as MenuItemConfig);
  }
  topSection.push({
    href: K.ROUTE.DASHBOARD_LIST,
    testId: ELEMENT.navDashboardsAllDashboards,
    itemAs: { Link },
    children: (
      <>
        <ListIcon fontSize="small" /> {K.ROUTE_NAME.DASHBOARD_LIST}
      </>
    ),
    text: K.ROUTE_NAME.DASHBOARD_LIST,
  } as unknown as MenuItemConfig);

  const sections: MenuItemConfig[][] = [];
  sections.push(topSection);
  sections.push(
    dashboardList.map((dashboard, index) => ({
      href: getDashboardUrl(dashboard.name),
      role: 'listitem',
      text: dashboard.name,
      testId: createTestId(ELEMENT.navDashboardsDashboard, index),
    })),
  );

  return (
    <>
      <Dropdown isNav className={isActive ? 'active' : ''} sections={sections} itemAs={Link} testId={testId}>
        Dashboards
      </Dropdown>
      <DashboardCreator
        showDialog={showCreateDialog}
        afterCreate={AFTER_CREATE.GOTO}
        dialogClose={(): void => setShowCreateDialog(false)}
      />
    </>
  );
};

export default NavBarDashboardsDropdown;
